/**
 * Factura de venta de chasis en hoja carta (8.5x11), con el mismo estilo de
 * casa que la factura de venta x mayor (impresionxmayor.js).
 *
 * Sirve para los dos tipos de venta que lista el informe de motores
 * facturados: al contado muestra la forma de pago y su referencia. El
 * detalle va en este orden: precio de venta (a crédito incluye el interés a
 * cobrar), ITBIS (si mostraritbis está activo, solo informativo: no se suma),
 * total (igual al precio de venta), inicial, balance (total - inicial) y
 * valor de la cuota (solo crédito). El
 * documento se titula CONDUCE. La venta a crédito no imprimía ningún
 * comprobante al registrarse, así que este es su primer documento: se emite
 * siempre como reimpresión desde el informe.
 */
import { imprimirHtml } from "lib/impresionhtml";
import { etiquetaDocumento } from "lib/documento";

const NAVY = "#18586b";

export const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const esc = (s) =>
  String(s ?? "").replace(/[<>&]/g, (ch) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[ch]));

// Logo del membrete: solo data URLs de imagen (lo que guarda configuración);
// cualquier otra cosa se ignora para no inyectar HTML en la impresión.
const logoImg = (comp) =>
  comp && typeof comp.logo === "string" && comp.logo.startsWith("data:image/")
    ? `<img class="logo" src="${comp.logo}" alt="" />`
    : "";

// Imagen de pie de página (misma regla que el logo): franja fija al fondo
// de la hoja, debajo de las firmas.
const footerHtml = (comp) =>
  comp && typeof comp.footer === "string" && comp.footer.startsWith("data:image/")
    ? `<div class="pie"><img src="${comp.footer}" alt="" /></div>`
    : "";

// Fila del bloque de montos; `destacado` la separa como total del documento.
const montoHtml = (etiqueta, valor, destacado = false) =>
  `<tr class="${destacado ? "destacado" : ""}">
     <td>${esc(etiqueta)}</td>
     <td class="num">RD$ ${fmtMoney(valor)}</td>
   </tr>`;

const datoHtml = (etiqueta, valor) =>
  valor ? `<div><span>${esc(etiqueta)}</span><b>${esc(valor)}</b></div>` : "";

/**
 * @param {Object} det          Respuesta de /api/facturamotor_detalle.
 * @param {Object} det.factura  Fila de factmot ya resuelta por el backend.
 * @param {Object} det.compania Membrete {nombre, rnc, direccion, telefono}.
 * @param {Object} [det.itbis]  {mostrar, tasa} de varinicial; con mostrar
 *                              activo se agrega la línea de ITBIS calculado
 *                              encima del precio de venta (precio × tasa%),
 *                              informativa: no se suma al total.
 */
export function imprimirFacturaMotor(det) {
  const f = det?.factura || {};
  const c = det?.compania || {};
  const esContado = (f.tipofactura || "") === "Contado";
  const cliente = [f.nombres, f.apellidos].filter(Boolean).join(" ");
  const vehiculo = [f.marca, f.modelo, f.colore].filter(Boolean).join(" · ");

  const num = (v) => {
    const n = typeof v === "number" ? v : parseFloat(String(v ?? "").replace(/,/g, ""));
    return Number.isNaN(n) ? 0 : n;
  };

  // A crédito el precio de venta mostrado incluye el interés a cobrar y las
  // ventas generales distribuidas en las cuotas (GPS, seguros, f.ventatotal),
  // fundidas sin desglose: así precio, balance y cuota × plazo cuadran entre
  // sí y con la deuda del expediente. El ITBIS (si mostraritbis está activo)
  // se calcula solo sobre el vehículo — las ventas distribuidas no inflan su
  // base — pero NO se suma al total: el total del conduce es el propio precio
  // de venta, y el balance le descuenta la inicial.
  const tasaItbis = Number(det?.itbis?.tasa) || 0;
  const ventaTotal = esContado ? 0 : num(f.ventatotal);
  const precioVehiculo = num(f.precio) + (esContado ? 0 : num(f.interesacobrar));
  const precioVenta = precioVehiculo + ventaTotal;
  const conItbis = det?.itbis?.mostrar === true && tasaItbis > 0 && precioVehiculo > 0;
  const montoItbis = conItbis ? (precioVehiculo * tasaItbis) / 100 : 0;
  const filasItbis = conItbis ? montoHtml(`ITBIS (${tasaItbis}%)`, montoItbis) : "";
  const total = precioVenta;
  const cantCuotas = Number(f.cuotas) || 0;
  const conInicialPactado = !esContado && f.completivo != null && num(f.inicial) > 0;
  // Sin inicial pactado (factmot.inicial = 0) la línea Inicial muestra lo
  // cobrado (abonos ligados a la factura) en vez de 0.00.
  const inicialMostrado = num(f.inicial) > 0 ? f.inicial : num(f.inicialpagado);

  const montos = esContado
    ? [
        montoHtml("Precio de venta", precioVenta),
        filasItbis,
        montoHtml("Total", total, true),
      ].join("")
    : [
        montoHtml("Precio de venta", precioVenta),
        filasItbis,
        montoHtml("Total", total, true),
        montoHtml("Inicial", inicialMostrado),
        // Completivos al inicial: el backend solo manda completivo con el
        // flag activo; pagado y pendiente se imprimen cuando hubo inicial
        // pactado. Es cuenta aparte: el balance sigue sobre el inicial pactado.
        conInicialPactado ? montoHtml("Inicial pagado", f.inicialpagado) : "",
        conInicialPactado ? montoHtml("Inicial pendiente", f.completivo) : "",
        // El balance NO incluye el ITBIS: la deuda real del préstamo (cuotas)
        // se calculó sobre precio+interés sin ITBIS, y el conduce debe cuadrar
        // con ella. El ITBIS queda solo en la línea informativa.
        montoHtml("Balance", precioVenta - num(f.inicial)),
        montoHtml(cantCuotas ? `Valor de la cuota (${cantCuotas} cuotas)` : "Valor de la cuota", f.vcuotas),
      ].join("");

  const html = `
    <html><head><meta charset="utf-8"><meta name="color-scheme" content="light"><title>Conduce</title>
    <style>
      @page { size: letter; margin: 14mm; }
      * { box-sizing: border-box; }
      body { background: #fff; color: #1a2430; font-family: Arial, Helvetica, sans-serif; font-size: 12px; margin: 0; }
      .cab { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px;
             border-bottom: 3px solid ${NAVY}; padding-bottom: 12px; }
      .emp { display: flex; align-items: flex-start; gap: 12px; }
      .emp img.logo { max-height: 90px; max-width: 250px; object-fit: contain; }
      .emp h1 { margin: 0; font-size: 20px; color: ${NAVY}; }
      .emp p { margin: 2px 0 0; color: #4a5866; font-size: 11px; }
      .doc { text-align: right; min-width: 190px; }
      .doc .titulo { font-size: 15px; font-weight: bold; color: ${NAVY}; letter-spacing: 0.5px; text-transform: uppercase; }
      .doc p { margin: 3px 0 0; font-size: 11px; color: #4a5866; }
      .doc b { color: #1a2430; }
      .bloque { margin: 14px 0; border: 1px solid #d7dee5; border-radius: 6px; padding: 10px 14px;
                display: flex; gap: 30px; flex-wrap: wrap; }
      .bloque span { font-size: 10px; color: #4a5866; text-transform: uppercase; letter-spacing: 0.05em; display: block; }
      .bloque b { font-size: 12px; }
      .rotulo { margin: 16px 0 4px; font-weight: bold; color: ${NAVY}; font-size: 12px; text-transform: uppercase; }
      table.montos { width: 55%; border-collapse: collapse; }
      table.montos td { padding: 6px 8px; border-bottom: 1px solid #e3e9ee; }
      td.num { text-align: right; }
      tr.destacado td { font-weight: bold; border-top: 2px solid ${NAVY}; border-bottom: none;
                        background: #f0f5f8; color: ${NAVY}; font-size: 14px; }
      .firmas { display: flex; justify-content: space-around; margin-top: 70px; }
      .firmas div { width: 210px; border-top: 1px solid #1a2430; text-align: center; padding-top: 5px;
                    font-size: 11px; color: #4a5866; }
      .pie { position: fixed; bottom: 0; left: 0; right: 0; }
      .pie img { display: block; width: 100%; }
    </style></head>
    <body>
      <div class="cab">
        <div class="emp">
          ${logoImg(c)}
          <div>
            <h1>${esc(c.nombre)}</h1>
            ${c.direccion ? `<p>${esc(c.direccion)}</p>` : ""}
            <p>${[c.rnc ? `RNC: ${esc(c.rnc)}` : "", c.telefono ? `Tel: ${esc(c.telefono)}` : ""]
              .filter(Boolean)
              .join(" · ")}</p>
          </div>
        </div>
        <div class="doc">
          <div class="titulo">Conduce</div>
          <p>${esContado ? "Contado" : "Crédito"}</p>
          <p># Factura: <b>${esc(f.id)}</b></p>
          <p>Fecha: <b>${esc(f.fecha)}</b></p>
          ${f.ncf ? `<p>NCF: <b>${esc(f.ncf)}</b></p>` : ""}
          ${f.noprest ? `<p># Préstamo: <b>${esc(f.noprest)}</b></p>` : ""}
        </div>
      </div>
      <div class="bloque">
        ${datoHtml("Cliente", cliente)}
        ${datoHtml(etiquetaDocumento(f.cedula), f.cedula)}
        ${datoHtml("Sucursal", f.descripcion)}
        ${datoHtml("Vendedor", f.vendedor)}
      </div>
      <div class="rotulo">Vehículo</div>
      <div class="bloque">
        ${datoHtml("Chasis", f.chasis)}
        ${datoHtml("Marca / Modelo / Color", vehiculo)}
        ${esContado ? datoHtml("Condición", f.condicion) : ""}
      </div>
      ${
        esContado && (f.banco || f.referencia)
          ? `<div class="rotulo">Forma de pago</div>
      <div class="bloque">
        ${
          Array.isArray(f.formas) && f.formas.length > 1
            ? f.formas
                .map((x) =>
                  datoHtml(
                    x.referencia ? `${x.tipopago} (${x.referencia})` : x.tipopago,
                    `RD$ ${fmtMoney(x.valor)}`
                  )
                )
                .join("")
            : `${datoHtml("Tipo de pago", f.banco)}
        ${datoHtml("Referencia", f.referencia)}`
        }
      </div>`
          : ""
      }
      <div class="rotulo">Detalle</div>
      <table class="montos">${montos}</table>
      <div class="firmas">
        <div>Preparado por</div>
        <div>Recibido por</div>
      </div>
      ${footerHtml(c)}
      <script>window.onload=function(){window.print();}</script>
    </body></html>`;
  imprimirHtml(html);
}

export default imprimirFacturaMotor;
